// C11 — Reusable notification row. Used by NotificationBell dropdown
// AND NotificationsPage. Behaviour is identical; the page passes
// `showArchive` so the per-row Archive button only appears there.

import React, { useState } from 'react';
import { Archive, ArchiveRestore } from 'lucide-react';
import { timeAgo } from '../../lib/notificationsApi';

const CATEGORY_DOT = {
  collaboration: 'bg-indigo-500',
  system: 'bg-gray-400',
  marketplace: 'bg-emerald-500',
  project: 'bg-violet-500',
  payment: 'bg-amber-500',
};

const PRIORITY_RING = {
  high: 'ring-2 ring-red-200',
  medium: '',
  low: '',
};

export default function NotificationItem({
  notif,
  // Action callbacks — the parent owns the mutation logic so list
  // refresh / optimistic updates / etc. happen in one place.
  onClickOpen,        // (notif) => void   — page navigation + mark-read
  onArchive = null,   // (id)    => void   — when omitted the button is hidden
  onUnarchive = null, // (id)    => void
  compact = false,    // dropdown variant has tighter padding
}) {
  const [hover, setHover] = useState(false);
  const dotClass = CATEGORY_DOT[notif.category] || CATEGORY_DOT.system;
  const ringClass = PRIORITY_RING[notif.priority] || '';

  return (
    <li
      className={[
        'group flex gap-3 items-start cursor-pointer transition-colors',
        compact ? 'px-3 py-2.5' : 'px-4 py-3',
        notif.read ? 'bg-white hover:bg-gray-50' : 'bg-indigo-50/40 hover:bg-indigo-50/70',
      ].join(' ')}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onClickOpen?.(notif)}
      data-testid={`notif-row-${notif.id}`}
    >
      {/* Unread dot + category color in one swatch */}
      <span className="flex-shrink-0 mt-1.5 relative">
        <span className={`block w-2 h-2 rounded-full ${dotClass} ${ringClass}`} />
        {!notif.read && (
          <span
            className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-indigo-600 ring-2 ring-white"
            data-testid={`notif-unread-${notif.id}`}
            aria-label="Unread"
          />
        )}
      </span>

      <div className="flex-1 min-w-0">
        <p className={[
          'text-sm leading-snug truncate',
          notif.read ? 'text-gray-700' : 'text-gray-900 font-medium',
        ].join(' ')}>
          {notif.title}
        </p>
        {notif.message && (
          <p className="text-xs text-gray-500 leading-snug mt-0.5 line-clamp-2">
            {notif.message}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1">
          <span className="text-[10px] text-gray-400 uppercase tracking-wide">
            {notif.category}
          </span>
          <span className="text-[10px] text-gray-300">·</span>
          <time className="text-[10px] text-gray-400" dateTime={notif.created_at}>
            {timeAgo(notif.created_at)}
          </time>
        </div>
      </div>

      {/* Per-row archive (Notifications page only). Hidden until hover
          so the row stays clean by default. */}
      {(onArchive || onUnarchive) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (notif.archived) onUnarchive?.(notif.id);
            else onArchive?.(notif.id);
          }}
          className={[
            'flex-shrink-0 p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-opacity',
            hover ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
          ].join(' ')}
          aria-label={notif.archived ? 'Unarchive' : 'Archive'}
          data-testid={`notif-archive-${notif.id}`}
        >
          {notif.archived ? <ArchiveRestore className="w-3.5 h-3.5" />
                          : <Archive className="w-3.5 h-3.5" />}
        </button>
      )}
    </li>
  );
}
